import React, { useMemo } from 'react';

import { Tag, TagPropTypes } from '@ui5/webcomponents-react';

import styles from './VersionLabel.module.css';

export type VersionLabelText = 'FREE' | 'FREE EDITION';

export interface VersionLabelProps {
  /*
   * The contents inside of the VersionLabel component.
   * Must be a single child.
   */
  children: React.ReactNode; // Allow only a single child
}

/**
 * Get the design based on the children of the VersionLabel component.
 * @param children - The children of the VersionLabel component.
 * @returns The design of the VersionLabel component.
 */
function getDesign(children: VersionLabelText): TagPropTypes['design'] {
  let design: TagPropTypes['design'] = 'Set2';
  if (children === 'FREE') {
    design = 'Set1';
  }

  return design;
}

/**
 * Get the color scheme based on the design.
 * @param design - The design of the VersionLabel component.
 * @returns The color scheme of the VersionLabel component.
 */
function getColorScheme(design: TagPropTypes['design']): TagPropTypes['colorScheme'] {
  // By default, the color scheme is '4', green color.
  let colorScheme: TagPropTypes['colorScheme'] = '4';
  if (design === 'Set1') {
    // Set to dark violet color.
    colorScheme = '7';
  }

  return colorScheme;
}

export function VersionLabel(props: VersionLabelProps): JSX.Element {
  const { children } = props;

  if (React.Children.count(children) !== 1) {
    throw new Error('VersionLabel expects exactly one child.');
  }

  const design = useMemo(() => getDesign(children as VersionLabelText), [children]);
  const colorScheme = useMemo(() => getColorScheme(design), [design]);

  return (
    <Tag
      className={styles['version-label']}
      colorScheme={colorScheme}
      design={design}
      hideStateIcon
    >
      {children}
    </Tag>
  );
}

VersionLabel.displayName = 'VersionLabel';
